import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';

/**
 * SMS Terms — companion legal page linked from the Lead Form Step 6
 * (Module 8 Section 5.3). Content is intentionally concise; expand in place
 * as the SMS program grows. Do NOT duplicate general Terms/Privacy content
 * here — link out for those.
 */
const SmsTermsPage = () => {
  const sections = [
    {
      title: '1. Program Description',
      content: `The Gleam Guru Cleaners SMS program sends transactional and service-related text messages to customers who explicitly opt in. Message types include quote-request follow-ups, appointment scheduling, appointment reminders, invoices, account updates, and customer support responses. This is a customer-service program, not a marketing/promotional program.`,
    },
    {
      title: '2. Opt-In',
      content: `You opt in by checking the SMS consent box on our quote-request form or by giving verbal/written consent to an authorized Gleam Guru representative. Opting in is entirely optional — the box is unchecked by default, and unchecking it does not prevent you from booking service. Providing a phone number alone does NOT constitute SMS consent.`,
    },
    {
      title: '3. Message Frequency',
      content: `Message frequency varies based on the number of appointments and service events tied to your account. There is no fixed cadence. In a typical month with one recurring appointment, expect no more than 5–10 messages.`,
    },
    {
      title: '4. Message & Data Rates',
      content: `Standard message and data rates from your mobile carrier may apply. Gleam Guru does not charge you directly for text messages. Check with your mobile carrier for details on your plan.`,
    },
    {
      title: '5. Opt-Out',
      content: `You may opt out of the SMS program at any time by replying STOP to any message. You will receive one final confirmation message. You may also contact hello@gleamgurus.com to be opted out manually. Opting out will not affect essential account communications delivered by other channels (email or phone).`,
    },
    {
      title: '6. Help',
      content: `Reply HELP to any message for assistance, or email hello@gleamgurus.com. For a full description of our data practices, see our Privacy Policy. For general service terms, see our Terms of Service.`,
    },
    {
      title: '7. Carriers & Availability',
      content: `Supported by major U.S. wireless carriers. Message delivery is subject to carrier and network availability and is not guaranteed. Gleam Guru is not liable for delayed or undelivered messages.`,
    },
    {
      title: '8. Privacy',
      content: `Phone numbers collected as part of the SMS program are used solely to deliver the messages described above. We do not sell or share your phone number with third parties for marketing purposes. See our Privacy Policy for full details.`,
    },
    {
      title: '9. Changes',
      content: `We may update these SMS Terms from time to time. The current version is always available at gleamgurus.com/sms-terms. Continued participation in the SMS program after an update constitutes acceptance of the revised terms.`,
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-50 py-16 px-4" data-testid="sms-terms-page">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            SMS Terms
          </h1>
          <p className="text-sm text-zinc-500 mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Last updated February 2026
          </p>
        </motion.div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 sm:p-10 space-y-6">
            {sections.map((s, i) => (
              <motion.section
                key={s.title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`sms-terms-section-${i + 1}`}
              >
                <h2 className="text-lg font-bold text-zinc-900 mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.title}</h2>
                <p className="text-sm text-zinc-700 whitespace-pre-line leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {s.content}
                </p>
              </motion.section>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SmsTermsPage;
